import * as _ from 'lodash';
import * as utils from './utils';
import * as constants from './commons/constants';
import * as gridService from './services/gridService';
import * as concurrencyCompanyService from './services/concurrencyCompanyService';
import * as servicesApi from './commons/testimServicesApi';
import { ArgError } from './errors';
import { getLogger } from './commons/logger';
import { reporter } from './reports/reporter';
import { registerExitHook } from './processHandler';
import { calculateCoverage } from './coverage/jsCoverage';
import { SeleniumPerfStats } from './commons/SeleniumPerfStats';
import { mapFilesToLocalDrive } from './services/localRCASaver';
import { TESTIM_CONCURRENT_WORKER_COUNT } from './commons/config';
import { featureFlagsService } from './commons/featureFlagsService';
import { OverrideTestDataBuilder } from './OverrideTestDataBuilder';
import { featureAvailabilityService } from './commons/featureAvailabilityService';
import type { AndroidApp, iOSApp } from 'clickim/src/background/mobile/types';
import type { RunnerOptions } from './runOptions';
import type { ExecutionList } from './runners/TestPlanRunner';
import type { TestResult, TestStatus } from 'clickim/src/common/models/TestResult';
import type { TestResultUpdates } from './runners/WorkerManager';
import type { TestWithCustomTestDataAndRunConfig } from 'services/src/commons/testListBuilder';
import type { RawMobileAppVersion } from 'services/src/commons/mongo/models/mobileAppVersion';

const logger = getLogger('test-run-status');
const gitBranch = utils.getEnvironmentGitBranch();
const gitCommit = process.env.GIT_COMMIT || process.env.CIRCLE_SHA1 || process.env.TRAVIS_COMMIT;
const gitRepoUrl = process.env.GIT_URL || process.env.CIRCLE_REPOSITORY_URL;
const runnerVersion = utils.getRunnerVersion();

type ExecConfig = TestRunStatus['execConfig'];
interface TestRunStatusResultConfig extends Omit<ExecConfig, 'browser'> {
    isBeforeTestPlan?: true;
    isAfterTestPlan?: true;
    testDataTotal: number | null;
    testDataIndex: number | null;
    baseUrl: string;
    testConfig: TestWithCustomTestDataAndRunConfig['overrideTestConfig'];
    browser: string;
    testDataUrl?: string;
    testData?: object;
    testDataPrioritized?: boolean;
    gridInfo?: gridService.SerializedGrid;
}
export interface TestRunStatusTestResult {
    workerId?: number;
    testId: string;
    status: utils.ValueOf<typeof constants['runnerTestStatus']>;
    runnerStatus?: utils.ValueOf<typeof constants['runnerStatus']>;
    name: string;
    resultId: string;
    testStatus: TestStatus;
    testCreatorName: string | undefined;
    testCreatorEmail: string | undefined;
    testOwnerName: string | undefined;
    testOwnerEmail: string | undefined;
    testLabels: string[];
    testSuites: string[];
    allLabels: string[] | undefined;
    config: TestRunStatusResultConfig;
    startTime?: number;
    failureReason?: string;
    success?: boolean;
    sessionId?: string;
    duration?: number;
    failurePath?: TestResult['failurePath'];
    resultUrl?: string;
    mobile?: { [key in 'isAppFromDevice' | 'isAppForIosVirtualDevice']?: boolean };
    assets?: { [key in 'screenshots' | 'consoleLogs' | 'networkLogs']?: string[] };
    runConfig?: TestWithCustomTestDataAndRunConfig['runConfig'];
}

async function runHook<F extends(...arg: any) => PromiseLike<any>>(fn: F | undefined, ...args: Parameters<F>) {
    if (!fn || typeof fn !== 'function') {
        return undefined;
    }

    try {
        const res = await fn(...args as Parameters<F>[]);
        return (res || {}) as Awaited<ReturnType<F>>;
    } catch (err) {
        logger.warn('failed to run hook', { err });
        throw new ArgError(`failed to run hook promise ${err.message}`);
    }
}

export class TestRunStatus {
    private testRunStatus: { [testResultId: string]: TestRunStatusTestResult };
    private exportsGlobal: object = {};
    private startTime: number | null = null;
    private beforeSuiteParams: object | undefined = {};
    private executionStartedPromise = Promise.resolve();
    private readonly execConfig;
    private readonly fileUserParamsData: object;
    private readonly userTestDataString: string | undefined;
    private userTestDataParams: object | object[] | undefined = {};
    private readonly seleniumPerfStats = new SeleniumPerfStats();
    constructor(private testInfoList: ExecutionList, private readonly options: RunnerOptions, testPlanId: string | null, private readonly branchToUse: string) {
        this.options.runParams ||= {};
        this.fileUserParamsData = this.options.userParamsData;
        this.userTestDataString = this.options.testDataString;

        const browserNames = utils.getUniqBrowsers(options, testInfoList as TestWithCustomTestDataAndRunConfig[]);
        const runnerMode = options.lightweightMode ? options.lightweightMode.type : options.mode;
        this.execConfig = {
            parallel: TESTIM_CONCURRENT_WORKER_COUNT || options.parallel || 1,
            browser: browserNames,
            gitBranch,
            gitCommit,
            gitRepoUrl,
            runnerVersion,
            gridHost: options.host || options.gridData?.host,
            testimBranch: branchToUse,
            canaryMode: options.canary,
            source: options.source,
            schedulerId: options.schedulerId,
            testPlanId,
            testPlans: options.testPlan,
            testLabels: options.label,
            testSuites: [...new Set((testInfoList as TestWithCustomTestDataAndRunConfig[]).flatMap(test => test.testSuites))],
            testNames: options.name,
            testIds: options.testId,
            testConfigs: options.testConfigNames,
            testConfigIds: options.testConfigIds,
            port: options.port,
            browserTimeout: options.browserTimeout,
            timeout: options.timeout,
            newBrowserWaitTimeout: options.newBrowserWaitTimeout,
            tunnel: options.tunnel,
            tunnelPort: options.tunnelPort,
            tunnelHostHeader: options.tunnelHostHeader,
            runnerMode,
            gridId: options.gridId || options.gridData?.gridId,
            gridName: options.grid || options.gridData?.name,
            gridType: options.gridData?.type,
            retentionDays: options.retentionDays,
            codeCoverageReportPath: options.codeCoverageReportPath,
            collectCodeCoverage: options.codeCoverageUrlFilter || options.collectCodeCoverage,
            sessionType: 'codeless',
        };

        this.testRunStatus = this.calcTestRunStatus();
    }

    public waitForExecutionStartedFinished() {
        return this.executionStartedPromise;
    }

    public getTestResult(resultId: string) {
        return this.testRunStatus[resultId] as TestRunStatusTestResult | undefined;
    }

    public async addRetryTestResult({
        newResultId, originalTestResultId, previousTestResultId, projectId, executionId, retryCount = 1,
    }: { newResultId: string; originalTestResultId?: string; previousTestResultId?: string; projectId: string; executionId: string; retryCount?: number }) {
        const orgTestResult = this.testRunStatus[originalTestResultId!] || {};
        const {
            config,
            testId,
            name,
            testStatus,
            testCreatorName,
            testCreatorEmail,
            testOwnerName,
            testOwnerEmail,
            testLabels,
            testSuites,
            allLabels,
        } = orgTestResult;

        const newTestResult = {
            originalTestResultId,
            previousTestResultId,
            config: _.cloneDeep(config),
            testId,
            status: 'QUEUED' as const,
            name,
            resultId: newResultId,
            retryCount,
            testStatus,
        };

        this.testRunStatus[newResultId] = {
            ...newTestResult,
            testCreatorName,
            testCreatorEmail,
            testOwnerName,
            testOwnerEmail,
            testLabels,
            testSuites,
            allLabels,
        };

        return servicesApi.addTestRetry({
            projectId,
            runId: executionId,
            testId,
            newResultId,
            originalTestResultId,
            previousTestResultId,
            testResult: newTestResult,
        });
    }

    private testStart(wid: number, executionId: string, resultId: string, isRerun: boolean | undefined) {
        const test = this.getTestResult(resultId)!;
        test.workerId = wid;
        reporter.onTestStarted(test, wid, isRerun, resultId);

        return test;
    }

    private async updateTestStatusRunning(test: TestRunStatusTestResult, executionId: string, testRetryKey: `${number}:${number}`) {
        const { project: projectId, remoteRunId, projectData } = this.options;
        if (this.options.lightweightMode?.onlyTestIdsNoSuite) {
            return this.executionStartedPromise;
        }

        let testDataUrl = '';

        try {
            testDataUrl = (await servicesApi.updateTestDataArtifact(projectId, test.testId, test.resultId, test.config.testData, projectData!.defaults)) || '';
        } catch (err) {
            logger.error('failed to upload test data artifact (runner)', { err });
        }
        const testConfig = _.cloneDeep(test.config);
        delete testConfig.testData;
        testConfig.testDataUrl = testDataUrl;
        await this.executionStartedPromise;
        return servicesApi.updateTestStatus(projectId, executionId, test.testId, test.resultId, 'RUNNING', { startTime: test.startTime, config: testConfig, remoteRunId, testRetryKey });
    }

    private async testStartReport(test: TestRunStatusTestResult, executionId: string, testRetryKey: `${number}:${number}`) {
        if (utils.isQuarantineAndNotRemoteRun(test, this.options)) {
            return undefined;
        }
        const globalParameters = this.exportsGlobal;
        try {
            const params = await runHook(this.options.beforeTest, { ...test, exportsGlobal: globalParameters, globalParameters }, this.options.userData!.loginData!.token!);
            const testDataFileParams = Array.isArray(this.userTestDataParams) ? {} : (this.userTestDataParams || {});
            const testDataPrioritized = test.config?.testDataPrioritized ? test.config.testData : {};
            test.config.testData = { ...test.config.testData, ...this.exportsGlobal, ...this.fileUserParamsData, ...testDataFileParams, ...testDataPrioritized, ...this.beforeSuiteParams, ...params };
            this.options.runParams![test.resultId] = test.config.testData;
            test.startTime = Date.now();
            await this.updateTestStatusRunning(test, executionId, testRetryKey);

            return test;
        } catch (err) {
            logger.error('Failed to start test', { err });
            throw err;
        }
    }

    public testStartAndReport(wid: number, executionId: string, resultId: string, isRerun: boolean | undefined, testRetryKey: `${number}:${number}`) {
        const test = this.testStart(wid, executionId, resultId, isRerun);
        return this.testStartReport(test, executionId, testRetryKey);
    }

    public onGridSlot(resultId: string, gridInfo: gridService.SerializedGrid) {
        const test = this.getTestResult(resultId)!;
        test.config.gridInfo = { ...gridInfo, key: undefined, user: undefined };
        logger.info('on get grid info', { gridInfo: test.config.gridInfo });
    }

    private reportTestStatus(workerId: number, result: TestResult, test: TestRunStatusTestResult, isRerun: boolean | undefined) {
        const { name, testId, testStatus } = test;
        const { resultId, success } = result;
        if (testStatus === constants.testStatus.EVALUATING && featureAvailabilityService.isTestStatusEnabled) {
            reporter.onTestIgnored(workerId, test, `test in ${constants.testStatus.EVALUATING} status`);
            return;
        }
        if (success) {
            reporter.onTestPassed(name);
            return;
        }
        reporter.onTestFailed(
            test,
            test.failureReason,
            utils.getTestUrl(this.options.editorUrl!, this.options.project, testId, resultId, this.branchToUse),
            testId,
            isRerun,
            resultId,
        );
    }

    private calcResultText(result: TestResult) {
        return result.success ? constants.runnerTestStatus.PASSED : constants.runnerTestStatus.FAILED;
    }

    public onTestIgnored(wid: number, resultId: string) {
        const test = this.getTestResult(resultId)!;
        reporter.onTestIgnored(wid, test, `test in ${constants.testStatus.QUARANTINE}`);
    }

    private monitorMemoryUsage(test: TestRunStatusTestResult) {
        const memoryUsage = utils.getMemorySnapshot();
        if (memoryUsage.isOverThreshold) {
            logger.info('memoryUsage is over threshold', {
                memoryUsage,
                testName: test.name,
                testId: test.testId,
                resultId: test.resultId,
                startTime: test.startTime,
                duration: test.duration,
                success: test.success,
                failureReason: test.failureReason,
                lightweightMode: this.options.lightweightMode,
                parallel: this.options.parallel,
                browser: this.options.browser,
            });
        }
    }

    private testEnd(wid: number, result: TestResult, executionId: string, sessionId: string, isRerun: boolean | undefined) {
        const test = this.getTestResult(result.resultId)!;

        const duration = (result.endTime! - result.startTime) || 0;
        test.sessionId = sessionId;
        test.startTime = result.startTime || test.startTime || Date.now();
        test.duration = duration;
        result.duration = duration;
        test.failureReason = result.failureReason || result.reason;
        result.failureReason = test.failureReason;
        test.failurePath = result.failurePath;
        test.resultId = result.resultId;
        test.success = result.success;

        if (this.options.saveRCALocally) {
            mapFilesToLocalDrive(test, logger);
        }

        test.resultUrl = utils.getTestUrl(this.options.editorUrl!, this.options.project, test.testId, test.resultId, this.branchToUse);
        test.status = this.calcResultText(result);

        result.status = test.status as any;
        result.name = test.name;
        result.testStatus = test.testStatus;
        result.testId ||= test.testId;
        (result as any).testCreatorName = test.testCreatorName;
        (result as any).testCreatorEmail = test.testCreatorEmail;
        (result as any).testOwnerName = test.testOwnerName;
        (result as any).testOwnerEmail = test.testOwnerEmail;
        result.testData = test.config && typeof test.config.testDataTotal === 'number' ? {
            total: test.config.testDataTotal,
            index: test.config.testDataIndex,
        } : {};

        this.reportTestStatus(wid, result, test, isRerun);
        this.monitorMemoryUsage(test);
        reporter.onTestFinished(test, wid, isRerun);

        const afterMerge = { ...this.exportsGlobal, ...result.exportsGlobal };

        this.exportsGlobal = afterMerge;
        return test;
    }

    private async testEndReport(test: TestRunStatusTestResult, executionId: string, result: TestResult, testResultUpdates: TestResultUpdates) {
        const globalParameters = result.exportsGlobal!;
        try {
            try {
                await runHook(this.options.afterTest, { ...test, exportsGlobal: globalParameters, globalParameters }, this.options.userData!.loginData!.token!);
            } catch (err) {
                logger.error('HOOK threw an error', { test: test.testId, err });
                // eslint-disable-next-line no-console
                console.error('HOOK threw an error', err); // show the customer that his hook failed.
            }
            if (this.options.lightweightMode?.onlyTestIdsNoSuite) {
                return undefined;
            }

            return await servicesApi.updateTestStatus(this.options.project, executionId, test.testId, test.resultId, 'FINISHED', {
                startTime: test.startTime,
                endTime: result.endTime,
                success: test.success,
                failureReason: test.failureReason,
                remoteRunId: this.options.remoteRunId,
                ...testResultUpdates,
            }, 5);
        } catch (err) {
            logger.error('Failed to update test finished', { err });
            throw err;
        }
    }

    public testEndAndReport(wid: number, result: TestResult, executionId: string, sessionId: string, isRerun: boolean | undefined, testResultUpdates: TestResultUpdates) {
        const test = this.testEnd(wid, result, executionId, sessionId, isRerun);
        return this.testEndReport(test, executionId, result, testResultUpdates);
    }

    private getMobileRunSkippedReason(isAppFromDevice: boolean, nativeApp?: RawMobileAppVersion | iOSApp | AndroidApp, gridType?: string) {
        let reason = '';
        const { APP_FROM_DEVICE, VIRTUAL_BUILD } = constants.MOBILE_RUN_SKIP_REASON;
        if (isAppFromDevice) {
            reason = APP_FROM_DEVICE;
        }
        if (utils.shouldBlockVirtualIosBuild(nativeApp, gridType)) {
            reason = VIRTUAL_BUILD;
        }
        return reason;
    }

    private calcTestRunStatus() {
        const { options, testInfoList } = this;
        const companyId = options.company!.companyId;
        const gridType = options.gridData?.type;
        const entries = (testInfoList as (TestWithCustomTestDataAndRunConfig & { isBeforeTestPlan?: true; isAfterTestPlan?: true })[]).map(testInfo => {
            const runConfig = options.browser ? utils.getRunConfigByBrowserName(options.browser, options.saucelabs, options.browserstack) : testInfo.runConfig;
            const isAppFromDevice = utils.isAppFromDeviceDisabled(featureFlagsService, gridType) && utils.isAppFromDevice(testInfo.nativeApp) && !options.appId;
            const shouldSkipForMobile = utils.isMobileProject(options) && (utils.shouldBlockVirtualIosBuild(testInfo.nativeApp, gridType) || isAppFromDevice);
            const shouldSkipTest = utils.isQuarantineAndNotRemoteRun(testInfo, options) || shouldSkipForMobile;
            const data = {
                testId: testInfo.testId,
                testOptimization: testInfo.testOptimization,
                status: shouldSkipTest ? constants.runnerTestStatus.SKIPPED : constants.runnerTestStatus.QUEUED,
                name: testInfo.name,
                resultId: testInfo.resultId,
                testStatus: testInfo.testStatus || constants.testStatus.DRAFT,
                testCreatorName: testInfo.creatorName,
                testCreatorEmail: testInfo.creatorEmail,
                testOwnerName: testInfo.testOwnerName,
                testOwnerEmail: testInfo.testOwnerEmail,
                testLabels: testInfo.testLabels,
                testSuites: testInfo.testSuites,
                allLabels: testInfo.allLabels,
                ...(shouldSkipForMobile && { reason: this.getMobileRunSkippedReason(isAppFromDevice, testInfo.nativeApp, gridType) }),
                config: {
                    ...this.execConfig,
                    companyId,
                    testData: testInfo.testData?.value || null,
                    testDataPrioritized: testInfo.testData?.prioritize || null,
                    isBeforeTestPlan: testInfo.isBeforeTestPlan,
                    isAfterTestPlan: testInfo.isAfterTestPlan,
                    testDataTotal: testInfo.testData?.total || null,
                    testDataIndex: testInfo.testData?.index || null,
                    baseUrl: options.baseUrl || testInfo.baseUrl || testInfo.testConfig.baseUrl,
                    testConfig: testInfo.overrideTestConfig || testInfo.testConfig,
                    browser: runConfig.browserValue.toLowerCase(),
                },
            };
            return [testInfo.resultId, data];
        });
        return Object.fromEntries(entries);
    }

    private async endKeepAlive(slotService: 'mongo' | 'redis', projectId: string, companyId: string) {
        if (slotService === 'redis') {
            await concurrencyCompanyService.keepAlive.end(companyId, projectId);
        }
        if (slotService === 'mongo') {
            await gridService.keepAlive.end(projectId);
        }
    }

    public async executionStart(executionId: string, projectId: string, startTime: number, testPlanName: string, testNames: string[]) {
        logger.info('execution started',
            {
                executionId,
                gridId: this.execConfig.gridId,
                gridHost: this.execConfig.gridHost,
                gridName: this.execConfig.gridName,
                gridType: this.execConfig.gridType,
                parallel: this.execConfig.parallel,
                runnerVersion: this.execConfig.runnerVersion,
                source: this.execConfig.source,
            });
        const { options } = this;
        const { remoteRunId, projectData, company: { companyId } = { }, slotService } = options;

        registerExitHook(() => Promise.all([
            this.endKeepAlive(slotService, projectId, companyId!),
            servicesApi.reportExecutionFinished(
                'ABORTED',
                executionId,
                projectId,
                false,
                undefined,
                remoteRunId,
                { gridName: this.options.grid },
            ),
        ]));

        this.startTime = startTime || Date.now();
        const runHooksProps = {
            projectId,
            executionId,
            ...(featureFlagsService.flags.testNamesToBeforeSuiteHook.isEnabled() && { testNames }),
        };

        const testDataString = this.userTestDataString;
        // eslint-disable-next-line @typescript-eslint/no-implied-eval
        const testDataParams = await runHook<any>(new Function(testDataString || ''));
        if (testDataParams) {
            this.userTestDataParams = testDataParams;
            if (Array.isArray(testDataParams)) {
                const overrideTestDataBuilder = new OverrideTestDataBuilder(testDataParams, _.cloneDeep(this.testInfoList as TestWithCustomTestDataAndRunConfig[]), projectId);
                this.testInfoList = overrideTestDataBuilder.overrideTestData(true);
            }
        }
        const params = await runHook(options.beforeSuite, runHooksProps);
        const overrideTestDataBuilder = new OverrideTestDataBuilder(params, _.cloneDeep(this.testInfoList as TestWithCustomTestDataAndRunConfig[]), projectId);
        this.testInfoList = overrideTestDataBuilder.overrideTestData();
        this.testRunStatus = this.calcTestRunStatus();
        this.beforeSuiteParams = params;

        const { testInfoList } = this;
        const beforeTests = [];
        const tests = [];
        const afterTests = [];
        for (const test of testInfoList as (TestWithCustomTestDataAndRunConfig & { isBeforeTestPlan?: true; isAfterTestPlan?: true })[]) {
            if (test.isBeforeTestPlan) {
                beforeTests.push(test);
                continue;
            }
            if (test.isAfterTestPlan) {
                afterTests.push(test);
                continue;
            }
            tests.push(test);
        }

        const reportExecutionStarted = async () => {
            const testResults = _.cloneDeep(this.testRunStatus);
            await utils.promiseMap(Object.keys(testResults), async testResultId => {
                const test = testResults[testResultId];
                const testData = test.config?.testData;
                const testId = test.testId;
                const testDataUrl = await servicesApi.updateTestDataArtifact(projectId, testId, testResultId, testData, projectData!.defaults);
                if (!testDataUrl) {
                    return;
                }
                delete test.config.testData;
                test.config.testDataUrl = testDataUrl;
            });
            const isLocalRun = Boolean(options.useLocalChromeDriver || options.useChromeLauncher);
            const executionStartedPromise = servicesApi.reportExecutionStarted({
                executionId,
                projectId,
                labels: testPlanName || [],
                startTime,
                executions: testResults,
                config: this.execConfig,
                resultLabels: options.resultLabels,
                remoteRunId: options.remoteRunId,
                localRunUserId: options.user,
                isLocalRun,
                intersections: options.intersections,
            });
            this.executionStartedPromise = executionStartedPromise;
            executionStartedPromise.catch(e => logger.error(e));
            return executionStartedPromise;
        };

        try {
            await reportExecutionStarted();
        } catch (err) {
            logger.error('Failed to start suite', { err });
            // eslint-disable-next-line no-console
            console.error('Failed to start test run. Please contact support@testim.io');
        }
        return { beforeTests, tests, afterTests };
    }

    public concatSeleniumPerfMarks(marks: { [keys in keyof SeleniumPerfStats['marks']]: number[] }) {
        _.chain(marks)
            .keys()
            .each((key) => {
                const typedKey = key as keyof SeleniumPerfStats['marks'];
                if (this.seleniumPerfStats.marks[typedKey]) {
                    this.seleniumPerfStats.marks[typedKey] = [...this.seleniumPerfStats.marks[typedKey], ...marks[typedKey]];
                }
            })
            .value();
    }

    public async executionEnd(executionId: string) {
        // @ts-expect-error type error worth fixing
        const tests = utils.groupTestsByRetries(this.testRunStatus);
        const total = tests.length;

        let passed = 0;
        let skipped = 0;
        let failedInEvaluatingStatus = 0;
        for (const { status, testStatus } of tests) {
            if (status === constants.runnerTestStatus.PASSED) {
                passed++;
            }
            if (status === constants.runnerTestStatus.SKIPPED) {
                skipped++;
            }
            if (status === constants.runnerTestStatus.FAILED && testStatus === constants.testStatus.EVALUATING) {
                failedInEvaluatingStatus++;
            }
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { seleniumPerfMarks, ...resultExtraData } = this.seleniumPerfStats.getStats();

        try {
            await runHook(this.options.afterSuite, {
                exportsGlobal: this.exportsGlobal,
                tests,
                total,
                passed,
                skipped,
            });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.log('check your callback handler on afterSuite Hook for syntax or exception errors');
            logger.warn('error while running afterSuite Hook', { err, projectId: this.options.projectData!.projectId, executionId: this.options.executionId });
        }
        const coverageSummary = await calculateCoverage(this.options, this.branchToUse, total, executionId);
        Object.assign(resultExtraData, { coverageSummary });

        if (this.options.lightweightMode?.onlyTestIdsNoSuite) {
            return undefined;
        }
        try {
            return await servicesApi.reportExecutionFinished(
                'FINISHED',
                executionId,
                this.options.project,
                total === (passed + skipped + failedInEvaluatingStatus),
                {
                    tmsSuppressReporting: this.options.tmsSuppressReporting,
                    tmsRunId: this.options.tmsRunId,
                    tmsCustomFields: this.options.tmsCustomFields,
                },
                this.options.remoteRunId,
                resultExtraData,
            );
        } catch (err) {
            logger.error('Failed to update suite finished', { err });
            throw err;
        }
    }

    public async markAllQueuedTests(executionId: string, status: TestRunStatusTestResult['status'], failureReason: string, success: boolean) {
        const queuedResultIds = Object.keys(this.testRunStatus).filter(resultId => this.getTestResult(resultId)!.status === 'QUEUED');

        await servicesApi.updateExecutionTests(
            executionId,
            ['QUEUED'],
            status,
            failureReason,
            success,
            this.startTime,
            null,
            this.options.project,
        );
        for (const resultId of queuedResultIds) {
            const test = this.getTestResult(resultId)!;
            test.status = status;
            test.failureReason = failureReason;
            test.success = success;
        }
        return this.testRunStatus;
    }
}
